// Helper functions for unified todo system
import { supabase } from '@/integrations/supabase/client';

export interface UnifiedTodo {
  id: string;
  title: string;
  description?: string;
  assigned_to?: string;
  created_by: string;
  location_id?: string;
  project_id?: string;
  opportunity_id?: string;
  depends_on_todo_id?: string;
  scope: 'personal_crm' | 'personal_charging' | 'team_crm' | 'team_charging' | 'team_all' | 'location' | 'project' | 'opportunity';
  is_shared: boolean;
  status: 'open' | 'in_progress' | 'completed' | 'cancelled' | 'blocked';
  priority: 'low' | 'medium' | 'high' | 'urgent';
  due_date?: string;
  completed_at?: string;
  created_at: string;
  updated_at: string;
  deleted_at?: string;
  charging_locations?: any;
  projects?: any;
  charging_opportunities?: any;
  profiles?: any;
  parent_todo?: any;
}

/**
 * Sorts todos by dependency chain
 * Parent todos (dependencies) appear before their dependents
 */
export function sortTodosByDependency(todos: UnifiedTodo[]): UnifiedTodo[] {
  const todoMap = new Map(todos.map(t => [t.id, t]));
  const sorted: UnifiedTodo[] = [];
  const visited = new Set<string>();
  const visiting = new Set<string>(); // For cycle detection
  
  function visit(todo: UnifiedTodo): void {
    if (visited.has(todo.id)) return;
    
    // Detect circular dependencies
    if (visiting.has(todo.id)) {
      console.warn('Circular dependency detected for todo:', todo.id);
      return;
    }
    
    visiting.add(todo.id);
    
    // First add the dependency (parent)
    if (todo.depends_on_todo_id) {
      const parent = todoMap.get(todo.depends_on_todo_id);
      if (parent) {
        visit(parent);
      }
    }
    
    visiting.delete(todo.id);
    visited.add(todo.id);
    sorted.push(todo);
  }
  
  todos.forEach(todo => visit(todo));
  return sorted;
}

/**
 * Gets the dependency chain for a todo
 * Returns array of parent todo IDs
 */
export function getDependencyChain(todo: UnifiedTodo, allTodos: UnifiedTodo[]): string[] {
  const chain: string[] = [];
  const todoMap = new Map(allTodos.map(t => [t.id, t]));
  let current = todo;
  
  while (current.depends_on_todo_id) {
    const parent = todoMap.get(current.depends_on_todo_id);
    if (!parent || chain.includes(parent.id)) break; // Prevent infinite loops
    chain.push(parent.id);
    current = parent;
  }
  
  return chain;
}

/**
 * Checks if a todo is blocked by incomplete dependencies
 */
export function isTodoBlocked(todo: UnifiedTodo, allTodos: UnifiedTodo[]): boolean {
  if (!todo.depends_on_todo_id) return false;
  
  const parent = allTodos.find(t => t.id === todo.depends_on_todo_id);
  return parent ? parent.status !== 'completed' : false;
}

/**
 * Groups todos by location or project
 */
export function groupTodosByContext(todos: UnifiedTodo[]): Record<string, UnifiedTodo[]> {
  return todos.reduce((acc, todo) => {
    let key = 'Overige';
    
    if (todo.location_id) {
      key = `location_${todo.location_id}`;
    } else if (todo.project_id) {
      key = `project_${todo.project_id}`;
    }
    
    if (!acc[key]) {
      acc[key] = [];
    }
    acc[key].push(todo);
    return acc;
  }, {} as Record<string, UnifiedTodo[]>);
}

/**
 * Filters todos assigned to a user
 */
export function getAssignedTodos(todos: UnifiedTodo[], userId: string): UnifiedTodo[] {
  return todos.filter(t => t.assigned_to === userId && !t.deleted_at);
}

/**
 * Filters todos created by a user
 */
export function getCreatedTodos(todos: UnifiedTodo[], userId: string): UnifiedTodo[] {
  return todos.filter(t => t.created_by === userId && !t.deleted_at);
}

/**
 * Gets priority color classes
 */
export function getPriorityColor(priority: string): string {
  switch (priority) {
    case 'urgent': return 'bg-destructive text-destructive-foreground';
    case 'high': return 'bg-destructive/80 text-destructive-foreground';
    case 'medium': return 'bg-warning text-warning-foreground';
    case 'low': return 'bg-muted text-muted-foreground';
    default: return 'bg-secondary text-secondary-foreground';
  }
}

/**
 * Gets status badge configuration
 */
export function getStatusBadge(status: string): { label: string; variant: any } {
  const badges: Record<string, { label: string; variant: any }> = {
    open: { label: 'Open', variant: 'default' },
    in_progress: { label: 'Bezig', variant: 'secondary' },
    completed: { label: 'Voltooid', variant: 'success' },
    cancelled: { label: 'Geannuleerd', variant: 'destructive' },
    blocked: { label: 'Geblokkeerd', variant: 'outline' },
  };
  return badges[status] || badges.open;
}

/**
 * Verifies that the unified_todos table exists in the database
 * Used for debugging during development
 */
export async function verifyUnifiedTodosTable(): Promise<boolean> {
  try {
    const { error } = await supabase
      .from('unified_todos' as any)
      .select('id')
      .limit(1);
    
    if (error) {
      console.error('[verifyUnifiedTodosTable] Table verification failed:', error);
      return false;
    }
    
    return true;
  } catch (e) {
    console.error('[verifyUnifiedTodosTable] Exception:', e);
    return false;
  }
}
